import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const BG_URL = "https://images.unsplash.com/photo-1511447333015-45b65e60f6d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyeSUyMG5lb258ZW58MHx8fHwxNzgwNzQ5NTc0fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [botInfo, setBotInfo] = useState(() => {
    try {
      const cached = localStorage.getItem('botInfo');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    axios.get(`${API}/api/bot/status`).then(r => {
      setBotInfo(r.data);
      try { localStorage.setItem('botInfo', JSON.stringify(r.data)); } catch {}
    }).catch(() => {});
  }, []);

  const botName = botInfo?.bot_name || 'Bot Dashboard';
  const botAvatar = botInfo?.bot_avatar || null;

  const handleDiscordLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/api/auth/login-url`);
      window.location.href = data.url;
    } catch (e) {
      setError('Failed to initiate login. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: `url(${BG_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-black/75" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md mx-4"
        data-testid="login-container"
      >
        <div className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-2xl p-8 shadow-2xl">
          {/* Logo + Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#5865F2] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(88,101,242,0.4)] overflow-hidden">
              {botAvatar ? (
                <img src={botAvatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <svg width="32" height="32" viewBox="0 0 71 55" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.44077 45.4204 0.52529C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.52529C25.5141 0.44359 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.292408 45.3914C0.297518 45.4562 0.334268 45.5182 0.385858 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6018 45.5576C70.6562 45.5182 70.6901 45.459 70.6952 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978Z"/>
                </svg>
              )}
            </div>
            <h1 className="text-3xl font-bold font-[Outfit] text-white mb-2">{botName}</h1>
            <p className="text-zinc-400 text-sm font-[Manrope]">
              Sign in with Discord to access your server analytics and bot controls.
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {[
              { icon: '🎵', label: 'Music Analytics' },
              { icon: '📊', label: 'Server Stats' },
              { icon: '🤖', label: 'Bot Control' },
              { icon: '🛡️', label: 'Moderation Log' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-2 bg-[var(--bg-hover)] rounded-lg px-3 py-2">
                <span className="text-base">{icon}</span>
                <span className="text-xs text-zinc-300 font-[Manrope]">{label}</span>
              </div>
            ))}
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm text-center" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            data-testid="discord-login-btn"
            onClick={handleDiscordLogin}
            disabled={loading}
            className="w-full bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-60 text-white font-semibold font-[Outfit] rounded-xl py-3.5 flex items-center justify-center gap-3 transition-all duration-200 shadow-[0_4px_15px_rgba(88,101,242,0.35)]"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <svg width="20" height="16" viewBox="0 0 71 55" fill="white">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.44077 45.4204 0.52529C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.52529C25.5141 0.44359 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.292408 45.3914C0.297518 45.4562 0.334268 45.5182 0.385858 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6018 45.5576C70.6562 45.5182 70.6901 45.459 70.6952 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978Z"/>
              </svg>
            )}
            {loading ? 'Redirecting...' : 'Login with Discord'}
          </button>

          <p className="text-center text-zinc-500 text-xs mt-4 font-[Manrope]">
            Only server owners and admins can access the dashboard.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
